import React, { useEffect } from "react";
import { Button, Card, Input, InputNumber, Result, Select, Space, Typography } from "antd";
import { NAME_CHANGE_FEE_USD } from "../../../utils/operationRequestNameChange.js";
import { useNavigate } from "react-router-dom";
import {
  OPERATION_REQUEST_MODES,
  PAYMENT_CURRENCY_OPTIONS,
} from "../operationRequestConstants.js";
import { useOperationRequestUi } from "../OperationRequestUiContext.jsx";
import OperationRequestLayout from "./OperationRequestLayout.jsx";
import OperationRequestHeader from "./OperationRequestHeader.jsx";
import OperationRequestSidebar from "./OperationRequestSidebar.jsx";
import OperationRequestFormCard from "./OperationRequestFormCard.jsx";
import OperationRequestStickyFooter from "./OperationRequestStickyFooter.jsx";
import { logOperationRequestPageLayoutTraceDev } from "./operationRequestPageTrace.js";
import styles from "./OperationRequestWorkspace.module.css";

const { Text } = Typography;

/**
 * @param {{
 *   valid: boolean,
 *   mode: string | null,
 *   mergedOrder: Record<string, unknown> | null,
 *   hasLinkedOrder?: boolean,
 *   operationContext: Record<string, unknown> | null,
 *   contextLoading: boolean,
 *   pageTitle: string,
 *   headerPills: { status: string, kind: string, supplierSource: string, dates: string },
 *   requestType: string | null,
 *   onRequestTypeChange: (v: string | null) => void,
 *   isNameChange: boolean,
 *   passengersForNameChange: Array<Record<string, unknown>>,
 *   nameChangePassengerId: string | number | null,
 *   onNamePassengerChange: (v: string | number | null) => void,
 *   selectedNamePassenger: Record<string, unknown> | null,
 *   nameChangeNewFirst: string,
 *   nameChangeNewLast: string,
 *   onNameFirstChange: (s: string) => void,
 *   onNameLastChange: (s: string) => void,
 *   nameChangeFeePreview: { amountIls?: number | null, exchangeRateUsed?: number | null } | null,
 *   paymentRequired: boolean,
 *   onPaymentToggle: (v: boolean) => void,
 *   paymentDraft: Record<string, unknown>,
 *   setPaymentDraft: (fn: (p: any) => any) => void,
 *   message: string,
 *   onMessageChange: (s: string) => void,
 *   onBackToOrderPick: () => void,
 *   onSubmitOperation: () => void,
 *   onGoToPaymentFirst?: () => void,
 *   submitting: boolean,
 *   paymentLinkBusy?: boolean,
 * }} props
 */
export default function OperationRequestPage({
  valid,
  mode,
  mergedOrder,
  hasLinkedOrder = true,
  operationContext,
  contextLoading,
  pageTitle,
  headerPills,
  requestType,
  onRequestTypeChange,
  isNameChange,
  passengersForNameChange,
  nameChangePassengerId,
  onNamePassengerChange,
  selectedNamePassenger,
  nameChangeNewFirst,
  nameChangeNewLast,
  onNameFirstChange,
  onNameLastChange,
  nameChangeFeePreview,
  paymentRequired,
  onPaymentToggle,
  paymentDraft,
  setPaymentDraft,
  message,
  onMessageChange,
  onBackToOrderPick,
  onSubmitOperation,
  onGoToPaymentFirst,
  submitting,
  paymentLinkBusy = false,
}) {
  const navigate = useNavigate();
  const { openOperationRequest } = useOperationRequestUi();

  useEffect(() => {
    if (!valid) return;
    logOperationRequestPageLayoutTraceDev({
      orderId: mergedOrder?.id ?? mergedOrder?.orderId ?? null,
      mode,
      requestType,
      withoutOrder: !hasLinkedOrder,
      breakpoint: typeof window !== "undefined" ? window.innerWidth : null,
    });
  }, [valid, mergedOrder, mode, requestType, hasLinkedOrder]);

  if (!valid) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }} dir="rtl">
        <Result
          status="warning"
          title="לא נבחרה הזמנה"
          subTitle="יש לבחור הזמנה דרך חלון הפנייה לאופרציה, או להמשיך ללא הזמנה בבירור כללי"
          extra={
            <Space>
              <Button type="primary" onClick={() => navigate("/p/dashboard/home")}>
                חזרה לדף הבית
              </Button>
              <Button
                onClick={() => {
                  navigate("/p/dashboard/home");
                  openOperationRequest();
                }}
              >
                פתח בחירת הזמנה
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  const nameChangeWithOrder = Boolean(isNameChange && hasLinkedOrder);
  const stickyUsd = nameChangeWithOrder ? NAME_CHANGE_FEE_USD : null;
  const stickyIls = nameChangeWithOrder ? nameChangeFeePreview?.amountIls ?? null : null;
  const stickyRate = nameChangeWithOrder ? nameChangeFeePreview?.exchangeRateUsed ?? null : null;

  return (
    <OperationRequestLayout
      header={
        <OperationRequestHeader
          onBack={onBackToOrderPick}
          title={pageTitle}
          backLabel={hasLinkedOrder ? "חזרה להזמנה" : "חזרה"}
          hideOrderPills={!hasLinkedOrder}
          orderStatus={headerPills.status}
          orderKindLabel={headerPills.kind}
          supplierSourceLabel={headerPills.supplierSource}
          dateRangeLabel={headerPills.dates}
        />
      }
      sidebar={
        hasLinkedOrder ? (
          <OperationRequestSidebar context={operationContext} loading={contextLoading} />
        ) : (
          <Card className={styles.surfaceCard} title="פנייה כללית">
            <Text type="secondary">
              פנייה לאופרציה ללא הזמנה מקושרת. פרטו את הבקשה בהודעה — צוות האופרציה יטפל בשיחה.
            </Text>
          </Card>
        )
      }
    >
      {mode === OPERATION_REQUEST_MODES.operation_request ? (
        <>
          <OperationRequestFormCard
            requestType={requestType}
            onRequestTypeChange={onRequestTypeChange}
            hasLinkedOrder={hasLinkedOrder}
            isNameChange={isNameChange}
            passengers={passengersForNameChange}
            nameChangePassengerId={nameChangePassengerId}
            onNamePassengerChange={onNamePassengerChange}
            selectedNamePassenger={selectedNamePassenger}
            nameChangeNewFirst={nameChangeNewFirst}
            nameChangeNewLast={nameChangeNewLast}
            onNameFirstChange={onNameFirstChange}
            onNameLastChange={onNameLastChange}
            nameChangeFeePreview={nameChangeFeePreview}
            paymentRequired={paymentRequired}
            onPaymentToggle={onPaymentToggle}
            paymentDraft={paymentDraft}
            setPaymentDraft={setPaymentDraft}
            message={message}
            onMessageChange={onMessageChange}
          />
          <OperationRequestStickyFooter
            visible
            amountUsd={stickyUsd}
            amountIls={stickyIls}
            exchangeRate={stickyRate}
            submitting={submitting}
            paymentLinkBusy={paymentLinkBusy}
            onSubmit={onSubmitOperation}
            isNameChange={nameChangeWithOrder}
            onGoToPayment={nameChangeWithOrder ? onGoToPaymentFirst : undefined}
          />
        </>
      ) : null}

      {mode === OPERATION_REQUEST_MODES.payment_link ? (
        <Card className={styles.surfaceCard} title="יצירת לינק תשלום">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Text type="secondary">סכום</Text>
              <InputNumber
                style={{ width: "100%", marginTop: 6 }}
                min={0}
                size="large"
                value={paymentDraft.amount}
                onChange={(v) =>
                  setPaymentDraft((p) => ({
                    ...p,
                    amount: v == null || v === "" ? null : Number(v),
                  }))
                }
              />
            </div>
            <div>
              <Text type="secondary">מטבע</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                size="large"
                value={paymentDraft.currency}
                onChange={(v) =>
                  setPaymentDraft((p) => ({
                    ...p,
                    currency: v || "ILS",
                  }))
                }
                options={PAYMENT_CURRENCY_OPTIONS}
              />
            </div>
            <div>
              <Text type="secondary">תיאור לתשלום</Text>
              <Input
                style={{ marginTop: 6 }}
                size="large"
                value={paymentDraft.description}
                onChange={(e) =>
                  setPaymentDraft((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="למשל: תוספת מזוודה, דמי ביטול"
              />
            </div>
            <div>
              <Text type="secondary">כמות נוסעים (אופציונלי)</Text>
              <InputNumber
                style={{ width: "100%", marginTop: 6 }}
                min={1}
                size="large"
                value={paymentDraft.passengerCount}
                onChange={(v) =>
                  setPaymentDraft((p) => ({
                    ...p,
                    passengerCount: v == null || v === "" ? null : Number(v),
                  }))
                }
              />
            </div>
            <Button type="primary" disabled block size="large">
              צור לינק תשלום
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              בשלב הבא יחובר ליוצר קישורי התשלום הקיים במערכת
            </Text>
          </Space>
        </Card>
      ) : null}
    </OperationRequestLayout>
  );
}
